import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { ConfirmProvider } from "@/context/ConfirmContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { DemoProvider } from "@/lib/demo";
import OfflineBanner from "@/components/OfflineBanner";
import usePendingBadge from "@/hooks/usePendingBadge";
import Landing from "@/pages/Landing";
import { Login, Register, ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/Auth";
import { GoogleOAuthProvider } from "@react-oauth/google";
import PaystackCallback from "@/pages/PaystackCallback";
import Kenya from "@/pages/Kenya";
import Workspace from "@/pages/Workspace";
import BuildHome from "@/pages/BuildHome";
import AppManager from "@/pages/AppManager";
import Project from "@/pages/Project";
import Pricing from "@/pages/Pricing";
import Marketplace from "@/pages/Marketplace";
import Hosting from "@/pages/Hosting";
import PrivateGate from "@/pages/PrivateGate";
import Mobile from "@/pages/Mobile";
import USSD from "@/pages/USSD";
import Media from "@/pages/Media";
import Admin from "@/pages/Admin";
import AdminGovernance from "@/pages/AdminGovernance";
import CommandCenter from "@/pages/CommandCenter";
import Features from "@/pages/Features";
import NotFound from "@/pages/NotFound";
import Profile from "@/pages/Profile";
import Legal from "@/pages/Legal";
import About from "@/pages/About";
import FAQs from "@/pages/FAQs";
import Contact from "@/pages/Contact";
import Billing from "@/pages/Billing";
import Hiring from "@/pages/Hiring";
import Shared from "@/pages/Shared";
import CookieConsent from "@/components/CookieConsent";
import CopilotDock from "@/components/CopilotDock";
import InstallPrompt from "@/components/InstallPrompt";

function Protected({ children, adminOnly }) {
  const { user } = useAuth();
  if (user === null) return <div className="k-canvas" style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}><span className="k-muted">Loading…</span></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (adminOnly && user.platform_role !== "admin") return <Navigate to="/workspace" replace />;
  return children;
}

function AppInner() {
  const { user } = useAuth();
  const { resolved } = useTheme();
  const location = useLocation();
  usePendingBadge();
  return (
    <DemoProvider isAdmin={user?.platform_role === "admin"}>
      <ConfirmProvider>
        {/* Me4 — one toast contract: consistent position, stacking (max 4, expandable) and dismiss. */}
        <Toaster theme={resolved} position="top-right" expand visibleToasts={4} closeButton gap={10} duration={4000} />
        <OfflineBanner />
        {/* Mi6 — brief, consistent page-transition on route change. */}
        <div className="k-page" key={location.pathname}>
          <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/shared/:token" element={<Shared />} />
          <Route path="/private/:deploymentId" element={<PrivateGate />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/faq" element={<FAQs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/payment/paystack/callback" element={<PaystackCallback />} />
          <Route path="/legal/:slug" element={<Legal />} />
          <Route path="/disclaimer" element={<Legal />} />
          <Route path="/terms" element={<Legal />} />
          <Route path="/privacy" element={<Legal />} />
          <Route path="/cookies" element={<Legal />} />
          <Route path="/accessibility" element={<Legal />} />
          <Route path="/ai-transparency" element={<Legal />} />
          <Route path="/acceptable-use" element={<Legal />} />
          <Route path="/workspace" element={<Protected><Workspace /></Protected>} />
          <Route path="/workspace/build" element={<Protected><BuildHome /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/workspace/apps" element={<Protected><AppManager /></Protected>} />
          <Route path="/marketplace" element={<Protected><Marketplace /></Protected>} />
          <Route path="/hosting" element={<Protected><Hosting /></Protected>} />
          <Route path="/mobile" element={<Protected><Mobile /></Protected>} />
          <Route path="/ussd" element={<Protected><USSD /></Protected>} />
          <Route path="/media" element={<Protected><Media /></Protected>} />
          <Route path="/billing" element={<Protected><Billing /></Protected>} />
          <Route path="/hiring" element={<Protected><Hiring /></Protected>} />
          <Route path="/workspace/project/:id" element={<Protected><Project /></Protected>} />
          <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
          <Route path="/admin/governance" element={<Protected adminOnly><AdminGovernance /></Protected>} />
          <Route path="/admin/command-center" element={<Protected adminOnly><CommandCenter /></Protected>} />
          <Route path="/features" element={<Features />} />
          <Route path="/kenya" element={<Kenya />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </div>
        <CopilotDock />
        <CookieConsent />
        <InstallPrompt />
      </ConfirmProvider>
    </DemoProvider>
  );
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function App() {
  const tree = (
    <ThemeProvider>
      <AuthProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <AppInner />
          </BrowserRouter>
        </CurrencyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
  return GOOGLE_CLIENT_ID
    ? <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{tree}</GoogleOAuthProvider>
    : tree;
}
